import { CodedProperty, getFileExtension, NamedProperty } from "sprint-common-web";

import { AncillaryAssetResponseItem } from "../../../common/api/master-api/assets/search/ancillary-requests-responses/SearchAncillaryAssetsResponse";
import { MovieAssetResponse } from "../../../common/api/master-api/assets/search/movie-requests-responses/SearchMoviesAssetsResponse";
import { TvSeriesAssetResponseItem } from "../../../common/api/master-api/assets/search/tv-series-requests-responses/SearchTvSeriesAssetsResponse";
import { Trimming } from "../../../common/api/master-api/qc/TrimmingResponse";
import { AssetOrigin } from "../../../common/types/AssetOrigin";
import { AssetStatus } from "../../../common/types/AssetStatus";
import { AssetType } from "../../../common/types/AssetType";
import { AuditInfo } from "../../../common/types/AuditInfo";
import { resolveContentTitle, resolveContentVersion } from "../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { DeliveryType } from "../../../common/types/DeliveryType";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../common/types/RecordType";
import { mapAirlinesToDisplay } from "../../../common/utils/mapAirlinesToDisplay";
import { getAssetFilePathOrDefault } from "../../common/utils/getAssetFilePathOrDefault";

export interface AssetItem {
    id: string;
    path: string;
    content: string;
    contentType: ContentReferenceType;
    contentSubtype: ContentSubtype;
    versions: string[];
    versionType: string;
    distributors: string;
    airlines: string;
    recordType: RecordType;
    extension?: string;
    createdAudit: CreatedAudit;
    updatedAudit: UpdatedAudit;
    status: AssetStatus;
    origin: AssetOrigin;
    originator?: NamedProperty;
    assetType: AssetType;
    deliveryType?: DeliveryType;
    trimming?: Trimming;
    details: AssetDetails;
    isTrailer: boolean;
}

export type CreatedAudit = Pick<AuditInfo, "createdDate" | "createdBy">;
export type UpdatedAudit = Pick<AuditInfo, "updatedDate" | "updatedBy">;

export type AssetDetails = VideoAssetDetails | SoundtrackAssetDetails | SubtitleAssetDetails;

export interface VideoAssetDetails {
    type: RecordType.Video;
    aspectRatio?: NamedProperty;
    embeddedSubtitle?: CodedProperty;
}

export interface SoundtrackAssetDetails {
    type: RecordType.Audio;
    soundtrack?: CodedProperty;
}

export interface SubtitleAssetDetails {
    type: RecordType.Subtitle;
    subtitle?: CodedProperty;
    subtitleType?: DynamicSubtitleType;
    inPreparation?: boolean;
}

type AssetResponse = MovieAssetResponse | TvSeriesAssetResponseItem | AncillaryAssetResponseItem;

export const mapToAssetsListItem = (responseItem: AssetResponse): AssetItem => ({
    id: responseItem.id,
    content: resolveContentTitle(responseItem.content),
    contentSubtype: responseItem.content.subtype,
    contentType: responseItem.content.type,
    versions: resolveContentVersion(responseItem.content),
    versionType: responseItem.versionType.name,
    distributors: responseItem.distributors.map((distributor: NamedProperty) => distributor.name).join(", "),
    airlines: mapAirlinesToDisplay(responseItem.brandings),
    recordType: responseItem.recordType,
    extension: responseItem.path ? getFileExtension(responseItem.path) : undefined,
    status: responseItem.status,
    originator: responseItem.originator,
    assetType: responseItem.assetType,
    path: getAssetFilePathOrDefault(responseItem.path),
    trimming: responseItem.trimming,
    deliveryType: responseItem.deliveryType,
    origin: responseItem.origin,
    createdAudit: {
        createdDate: responseItem.audit.createdDate,
        createdBy: responseItem.audit.createdBy
    },
    updatedAudit: {
        updatedDate: responseItem.audit.updatedDate,
        updatedBy: responseItem.audit.updatedBy
    },
    details: getAssetDetails(responseItem),
    isTrailer: responseItem.isTrailer
});

const getAssetDetails = (responseItem: AssetResponse): AssetDetails => {
    switch (responseItem.recordType) {
        case RecordType.Video:
            return {
                type: RecordType.Video,
                embeddedSubtitle: responseItem.subtitles,
                aspectRatio: responseItem.aspectRatio
            };
        case RecordType.Audio:
            return {
                type: RecordType.Audio,
                soundtrack: responseItem.soundtrack
            };
        case RecordType.Subtitle:
            return {
                type: RecordType.Subtitle,
                subtitle: responseItem.subtitles
                    ? {
                          id: responseItem.subtitles.id,
                          code: responseItem.subtitles.code,
                          name: responseItem.subtitles.name
                      }
                    : undefined,
                subtitleType: responseItem.subtitles?.dynamicSubtitleType,
                inPreparation: responseItem.inPreparation
            };
    }
};

export interface TvSeriesAssetsListEpisodeItem {
    id: string;
    title: string;
    number: number;
}
